import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';

export const KNOWN_PLACEHOLDERS = [
  'first_name',
  'last_name',
  'company',
  'age',
  'years_of_service'
] as const;

const PLACEHOLDER_REGEX = new RegExp(String.raw`\{\{(?:${KNOWN_PLACEHOLDERS.join('|')})\}\}`, 'g');

type Token = { type: 'text' | 'chip'; value: string };

const splitTokens = (text: string): Token[] => {
  if (!text) return [];
  const tokens: Token[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
    const start = match.index ?? 0;
    if (start > lastIndex) tokens.push({ type: 'text', value: text.slice(lastIndex, start) });
    tokens.push({ type: 'chip', value: match[0] });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) tokens.push({ type: 'text', value: text.slice(lastIndex) });
  return tokens;
};

const buildPlaceholderSpan = (
  placeholder: string,
  labelFor: (placeholder: string) => string
): HTMLSpanElement => {
  const span = document.createElement('span');
  span.dataset.placeholder = placeholder;
  span.contentEditable = 'false';
  span.className = 'select-none text-base';
  span.textContent = `{{${labelFor(placeholder)}}}`;
  return span;
};

const tokensToFragment = (
  text: string,
  labelFor: (placeholder: string) => string
): DocumentFragment => {
  const frag = document.createDocumentFragment();
  splitTokens(text).forEach((token) => {
    if (token.type === 'chip') {
      frag.appendChild(buildPlaceholderSpan(token.value, labelFor));
    } else {
      const parts = token.value.split('\n');
      parts.forEach((part, idx) => {
        if (part) frag.appendChild(document.createTextNode(part));
        if (idx < parts.length - 1) frag.appendChild(document.createElement('br'));
      });
    }
  });
  return frag;
};

const serialize = (root: HTMLElement): string => {
  let out = '';
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? '';
    } else if (node instanceof HTMLElement) {
      const token = node.dataset.placeholder;
      out += token ?? (node.tagName === 'BR' ? '\n' : '');
    }
  });
  return out;
};

export interface PlaceholderEditorHandle {
  insertPlaceholder: (placeholder: string) => void;
  focus: () => void;
}

interface PlaceholderEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  labelFor: (placeholder: string) => string;
  ariaLabel?: string;
}

export const PlaceholderEditor = forwardRef<PlaceholderEditorHandle, PlaceholderEditorProps>(
  ({ value, onChange, onBlur, placeholder, hasError, className, labelFor, ariaLabel }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string | null>(null);

    useLayoutEffect(() => {
      const root = editorRef.current;
      if (!root || lastValueRef.current === value) return;
      root.replaceChildren(tokensToFragment(value, labelFor));
      lastValueRef.current = value;
    }, [value, labelFor]);

    const emit = () => {
      const root = editorRef.current;
      if (!root) return;
      const next = serialize(root);
      lastValueRef.current = next;
      onChange(next);
    };

    const replaceSelectionWith = (node: Node) => {
      const root = editorRef.current;
      if (!root) return;
      root.focus();
      const selection = globalThis.getSelection();
      let range: Range;
      if (selection && selection.rangeCount > 0 && root.contains(selection.anchorNode)) {
        range = selection.getRangeAt(0);
        range.deleteContents();
      } else {
        range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
      }
      const caretAnchor = node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild : node;
      range.insertNode(node);
      if (caretAnchor) {
        range.setStartAfter(caretAnchor);
        range.setEndAfter(caretAnchor);
      }
      selection?.removeAllRanges();
      selection?.addRange(range);
    };

    useImperativeHandle(ref, () => ({
      insertPlaceholder: (token: string) => {
        replaceSelectionWith(buildPlaceholderSpan(token, labelFor));
        emit();
      },
      focus: () => editorRef.current?.focus()
    }));

    const tryRemoveAdjacentPlaceholder = (range: Range, direction: -1 | 1): boolean => {
      if (!range.collapsed) return false;
      const root = editorRef.current;
      if (!root) return false;
      const node = range.startContainer;
      let candidate: Node | null = null;
      if (node.nodeType === Node.TEXT_NODE) {
        const atEdge =
          direction === -1
            ? range.startOffset === 0
            : range.startOffset === (node.textContent?.length ?? 0);
        if (atEdge) candidate = direction === -1 ? node.previousSibling : node.nextSibling;
      } else if (node === root) {
        candidate = root.childNodes[range.startOffset + (direction === -1 ? -1 : 0)];
      }
      if (candidate instanceof HTMLElement && candidate.dataset.placeholder) {
        candidate.remove();
        return true;
      }
      return false;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      const range = globalThis.getSelection()?.getRangeAt(0);
      if (!range) return;
      const dir = e.key === 'Backspace' ? -1 : 1;
      if (tryRemoveAdjacentPlaceholder(range, dir)) {
        e.preventDefault();
        emit();
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      replaceSelectionWith(tokensToFragment(text, labelFor));
      emit();
    };

    const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>, deleteAfter: boolean) => {
      const root = editorRef.current;
      const selection = globalThis.getSelection();
      if (!root || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (range.collapsed || !root.contains(range.commonAncestorContainer)) return;
      const tmp = document.createElement('div');
      tmp.appendChild(range.cloneContents());
      e.preventDefault();
      e.clipboardData.setData('text/plain', serialize(tmp));
      if (deleteAfter) {
        range.deleteContents();
        emit();
      }
    };

    return (
      <div
        ref={editorRef}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        tabIndex={0}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onCopy={(e) => handleCopy(e, false)}
        onCut={(e) => handleCopy(e, true)}
        onPaste={handlePaste}
        data-placeholder-text={value ? undefined : placeholder}
        className={`min-h-[12rem] w-full rounded-lg p-3 text-base outline-none whitespace-pre-wrap break-words bg-grey-50 border ${
          hasError ? 'border-error-main' : 'border-transparent'
        } focus-within:border-primary-main empty:before:content-[attr(data-placeholder-text)] empty:before:text-grey-500 ${
          className ?? ''
        }`}
      />
    );
  }
);

PlaceholderEditor.displayName = 'PlaceholderEditor';
